import React, { useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/template-api';

import { useFormState } from '../../../../hooks/useFormState';
import { updateOrderKeys } from '../../../../helpers/insertItemAtSpecificPositionWithOrderUpdate';

import { normalizeMarkdown, prepareMarkdownOnLoad } from '../../../../helpers/normalizeMarkdown';

import { defaultErrorMessage } from '../../../../constants/errorMessages';

import Modal from '../../../../design-system/Modal/Modal';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import NewRichTextArea from '../../../../design-system/NewRichTextArea/NewRichTextArea';
import { Button, Input, Select } from '../../../../design-system';

const TYPE_OPTIONS = [{ id: 'text', name: 'Text' }];

const ContentEditorModal = ({
    mode = 'create',
    data,
    contentArray,
    playbookTemplateId,
    setContent,
    onClose,
}) => {
    const { formData, handleInputChange } = useFormState(() => {
        if (mode === 'edit' && data) {
            const { name, type, detail } = data;
            return { name, type, text: prepareMarkdownOnLoad(detail?.text) };
        }
        return { name: '', type: 'text', text: '' };
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const allRequiredFieldsFilled = Object.values(formData)?.every((field) => !!field?.trim());
    const submitButtonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled
        ? 'default'
        : 'disabled';

    const getRequestBody = () => {
        const { name, type, text } = formData;
        const formattedContent = {
            name,
            type,
            detail: { text: normalizeMarkdown(text) },
        };

        let newContentArray = contentArray;

        if (mode === 'create') {
            const newContent = { ...formattedContent, order: (contentArray.length || 0) + 1 };
            newContentArray = [...contentArray, newContent];
        }

        if (mode === 'edit') {
            newContentArray = contentArray.map((item) =>
                item.order === data.order ? { ...item, ...formattedContent } : item
            );
        }

        const formattedNewContentArray = updateOrderKeys(newContentArray);
        return { details: { content: formattedNewContentArray } };
    };

    const handleSubmit = async () => {
        if (!allRequiredFieldsFilled) return;

        try {
            setIsLoading(true);

            const requestBody = getRequestBody();
            const { data } = await client.patch(
                API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
                requestBody
            );

            const content = data.default_version?.details?.content || [];
            setContent(content);

            onClose();
        } catch (e) {
            setIsLoading(false);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    const title = {
        create: 'Add Content',
        edit: 'Edit Content',
    }[mode];

    const submitButtonText = {
        create: 'Add',
        edit: 'Save',
    }[mode];

    return (
        <Modal size="medium" onClose={onClose} resetPadding>
            <ModalHeader title={title} onClose={onClose} outlined />

            <div className="py-5 px-5 sm:px-6 md:px-8 flex flex-col gap-4">
                <Select
                    name="content_type"
                    size="xs"
                    options={TYPE_OPTIONS}
                    value={formData.type}
                    label="Content Type"
                    isRequired
                    onChange={(type) => handleInputChange('type', type)}
                />

                <Input
                    size="xs"
                    value={formData.name}
                    name="content_name"
                    label="Content Title"
                    isRequired
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Give this content a clear, unique name"
                />

                <NewRichTextArea
                    value={formData.text}
                    label="Content Text"
                    isRequired
                    setValue={(text) => handleInputChange('text', text)}
                    placeholder="Fill out the text for this content section"
                    minHeight={200}
                    darkBorderColor
                />

                <div className="flex items-center justify-between gap-3">
                    <Button type="neutral" size="md" text="Cancel" onClick={onClose} />
                    <Button
                        type="secondary"
                        size="md"
                        text={submitButtonText}
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default ContentEditorModal;
